/**
 * What voice sounds like, for both surfaces (D29: voice is the one live
 * channel, so it has to be excellent), and the one reading of a capture error.
 *
 * - **Capture**: the browser's echo cancellation, noise suppression and auto
 *   gain, mono. The children wear headphones (constitution), and echo
 *   cancellation still guards the laptop whose headphones are off.
 * - **Send**: Opus at LiveKit's `music` preset (48 kbit/s mono: speech with
 *   room to spare; eight children at that rate is nothing), DTX so silence
 *   costs nothing, and RED (redundant frames) so a lost packet on a home
 *   Wi-Fi is not a lost syllable.
 * - **Rooms**: audio only, so no adaptive stream and no dynacast; the local
 *   track is stopped when it is unpublished, so no mic stays open behind a
 *   released take.
 */
import { AudioPresets, type AudioCaptureOptions, type RoomOptions, type TrackPublishOptions, Track } from "livekit-client";

const DEVICE_KEY = "ugis.voice.mic";

export function savedMic(): string | undefined {
  try {
    return localStorage.getItem(DEVICE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export function saveMic(deviceId: string): void {
  try {
    localStorage.setItem(DEVICE_KEY, deviceId);
  } catch {
    /* a private window: the choice lasts this page */
  }
}

export function capture(deviceId = savedMic()): AudioCaptureOptions {
  return {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    channelCount: 1,
    ...(deviceId ? { deviceId } : {}),
  };
}

export const PUBLISH: TrackPublishOptions = {
  source: Track.Source.Microphone,
  audioPreset: AudioPresets.music,
  dtx: true,
  red: true,
  forceStereo: false,
};

export function roomOptions(): RoomOptions {
  return {
    adaptiveStream: false,
    dynacast: false,
    audioCaptureDefaults: capture(),
    publishDefaults: { audioPreset: AudioPresets.music, dtx: true, red: true },
    stopLocalTrackOnUnpublish: true,
    disconnectOnPageLeave: true,
  };
}

export type MicProblem = "denied" | "none" | "busy" | "other";

/** A getUserMedia failure, read once for both surfaces. */
export function micProblem(e: unknown): MicProblem {
  const name = e instanceof Error || (typeof e === "object" && e !== null && "name" in e) ? String((e as { name?: string }).name) : "";
  if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") return "denied";
  if (name === "NotFoundError" || name === "OverconstrainedError" || name === "DevicesNotFoundError") return "none";
  if (name === "NotReadableError" || name === "TrackStartError" || name === "AbortError") return "busy";
  return "other";
}

/** Plain words for her and for him. */
export const MIC_WORDS: Record<MicProblem, string> = {
  denied: "Naršyklė neleidžia naudoti mikrofono. Spustelėk spynelę adreso juostoje, leisk mikrofoną ir bandyk dar kartą.",
  none: "Mikrofono nerasta. Prijunk ausines su mikrofonu ir bandyk dar kartą.",
  busy: "Mikrofoną dabar naudoja kita programa. Uždaryk ją ir bandyk dar kartą.",
  other: "Mikrofonas neįsijungė. Bandyk dar kartą.",
};

/** Retry delays for a room that dropped and did not come back by itself. */
export function backoff(attempt: number): number {
  return Math.min(15_000, 500 * 2 ** Math.min(attempt, 5));
}
